/**
 * Financial Aid API Hooks
 *
 * RTK Query wrappers preserving existing hook signatures while routing all
 * requests through the domain-router RTK layer.
 */
import { useCallback, useState } from 'react';
import type {
  DomainRouterMutationArgs,
  DomainRouterMutationData,
  DomainRouterMutationName,
  DomainRouterQueryArgs,
  DomainRouterQueryData,
  DomainRouterQueryName,
} from '../../api/domainRouterClient';
import { useDomainRouterMutation, useDomainRouterQuery } from '../../state/domainRouterHooks';

const serializeInput = (input: unknown) => (typeof input === 'string' ? input : JSON.stringify(input));

type FinancialAidApiErrorWithCode = Error & {
  code?: string;
};

const readErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const candidate = (error as { code?: unknown }).code;
  return typeof candidate === 'string' && candidate.trim() ? candidate.trim() : undefined;
};

const attachErrorCode = (error: Error, code: string | undefined): Error => {
  if (!code) {
    return error;
  }
  (error as FinancialAidApiErrorWithCode).code = code;
  return error;
};

const toError = (error: unknown): Error | null => {
  if (!error) {
    return null;
  }
  if (error instanceof Error) {
    return attachErrorCode(error, readErrorCode(error));
  }
  if (typeof error === 'object' && error && 'message' in error) {
    return attachErrorCode(
      new Error(String((error as { message?: unknown }).message ?? 'Unknown error')),
      readErrorCode(error),
    );
  }
  return new Error(String(error));
};

// Shared key registry for Financial Aid data surfaces.
export const financialAidKeys = {
  all: ['financial-aid'] as const,
  applications: () => [...financialAidKeys.all, 'applications'] as const,
  application: (id: string) => [...financialAidKeys.applications(), id] as const,
  applicationsByApplicant: (applicantId: string) => [...financialAidKeys.applications(), 'applicant', applicantId] as const,
  applicationForReview: (id: string) => [...financialAidKeys.applications(), 'review', id] as const,
  applicationsForReview: (filters?: unknown) => [...financialAidKeys.applications(), 'list', filters] as const,
  openCycle: (academicYear?: string) => [...financialAidKeys.all, 'open-cycle', academicYear] as const,
  familyLookup: (email?: string, lastName?: string, academicYear?: string) => [...financialAidKeys.all, 'family-lookup', email, lastName, academicYear] as const,
  parentStatus: (id: string) => [...financialAidKeys.all, 'parent-status', id] as const,
  documentStatus: (id: string) => [...financialAidKeys.all, 'documents', id] as const,
  statusHistory: (id: string) => [...financialAidKeys.all, 'status-history', id] as const,
  appeals: () => [...financialAidKeys.all, 'appeals'] as const,
  appealHistory: (id: string) => [...financialAidKeys.appeals(), 'history', id] as const,
  pendingAppeals: (filters?: unknown) => [...financialAidKeys.appeals(), 'pending', filters] as const,
  appealSummary: (id: string) => [...financialAidKeys.appeals(), 'summary', id] as const,
  reports: () => [...financialAidKeys.all, 'reports'] as const,
  report: (type: string, filters?: unknown) => [...financialAidKeys.reports(), type, filters] as const,
  applicationReportArtifacts: (id: string) => [...financialAidKeys.all, 'application-report-artifacts', id] as const,
};

type QueryHookResult<TData> = {
  data: TData | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
};

type DomainQueryAdapterState = {
  data?: unknown;
  isLoading: boolean;
  isFetching: boolean;
  error?: unknown;
  refetch: () => Promise<unknown>;
};

type DomainMutationAdapterState = {
  data?: unknown;
  isLoading: boolean;
  error?: unknown;
  reset: () => void;
};

const useUntypedDomainRouterQuery = useDomainRouterQuery as (
  operation: DomainRouterQueryName,
  args: unknown,
  options?: { skip?: boolean }
) => DomainQueryAdapterState;

const useUntypedDomainRouterMutation = useDomainRouterMutation as (
  operation: DomainRouterMutationName
) => readonly [((args: unknown) => Promise<unknown>), DomainMutationAdapterState];

const useQueryAdapter = <Name extends DomainRouterQueryName>(
  operation: Name,
  args: DomainRouterQueryArgs<Name>,
  enabled = true
): QueryHookResult<DomainRouterQueryData<Name>> => {
  const result = useUntypedDomainRouterQuery(operation, args, { skip: !enabled });

  const refetch = useCallback(async () => {
    await result.refetch();
  }, [result]);

  return {
    data: result.data as DomainRouterQueryData<Name> | undefined,
    isLoading: result.isLoading || result.isFetching,
    error: toError(result.error),
    refetch,
  };
};

type MutationAdapterResult<TInput, TOutput> = {
  mutateAsync: (input: TInput) => Promise<TOutput>;
  isPending: boolean;
  error: Error | null;
  data: TOutput | undefined;
  reset: () => void;
};

type JsonInputMutationName = {
  [Name in DomainRouterMutationName]: DomainRouterMutationArgs<Name> extends { input: unknown }
    ? Name
    : never;
}[DomainRouterMutationName];

type JsonMutationInput<Name extends JsonInputMutationName> =
  DomainRouterMutationArgs<Name> extends { input: infer Input } ? Input : never;

const useJsonInputMutation = <
  Name extends JsonInputMutationName,
  TInput = JsonMutationInput<Name>,
  TOutput = DomainRouterMutationData<Name>,
>(operation: Name): MutationAdapterResult<TInput, TOutput> => {
  const [mutate, state] = useUntypedDomainRouterMutation(operation);

  const mutateAsync = useCallback(
    async (input: TInput) =>
      (await mutate({
        input: serializeInput(input),
      } as DomainRouterMutationArgs<Name>)) as TOutput,
    [mutate]
  );

  return {
    mutateAsync,
    isPending: state.isLoading,
    error: toError(state.error),
    data: state.data as TOutput | undefined,
    reset: state.reset,
  };
};

// ============================================================================
// Parent Operations
// ============================================================================

export function useFinancialAidApplication(applicationId: string | undefined) {
  return useQueryAdapter('getFinancialAidApplication', { applicationId: applicationId ?? '' }, Boolean(applicationId));
}

type UseFinancialAidByApplicantOptions = boolean | {
  enabled?: boolean;
  academicYear?: string | null;
  limit?: number;
};

export function useFinancialAidByApplicant(
  applicantId: string | undefined,
  options: UseFinancialAidByApplicantOptions = true,
) {
  const enabled = typeof options === 'boolean' ? options : options.enabled ?? true;
  const academicYear = typeof options === 'boolean'
    ? ''
    : String(options.academicYear ?? '').trim();
  const limit = typeof options === 'boolean'
    ? 50
    : options.limit ?? 50;

  return useQueryAdapter(
    'getFinancialAidByApplicant',
    {
      applicantId: applicantId ?? '',
      limit,
      ...(academicYear ? { academicYear } : {}),
    },
    Boolean(applicantId) && enabled,
  );
}

export function useFinancialAidOpenCycle(academicYear?: string, enabled = true) {
  const normalizedAcademicYear = String(academicYear ?? '').trim();
  return useQueryAdapter(
    'getFinancialAidOpenCycle',
    normalizedAcademicYear ? { academicYear: normalizedAcademicYear } : {},
    enabled,
  );
}

type FinancialAidFamilyLookupHookInput = {
  email?: string;
  lastName?: string;
  familyName?: string;
  academicYear?: string;
  enabled?: boolean;
};

export function useFinancialAidFamilyLookup(
  input: FinancialAidFamilyLookupHookInput | string | undefined,
  lastName?: string,
  enabled = true,
) {
  const lookupInput: FinancialAidFamilyLookupHookInput = typeof input === 'object' && input
    ? input
    : { email: input, lastName, enabled };
  const normalizedEmail = String(lookupInput.email ?? '').trim().toLowerCase();
  const normalizedLastName = String(lookupInput.lastName ?? lookupInput.familyName ?? '').trim();
  const normalizedAcademicYear = String(lookupInput.academicYear ?? '').trim();
  const shouldRun = (lookupInput.enabled ?? enabled) && Boolean(normalizedEmail || normalizedLastName);

  return useQueryAdapter(
    'lookupFinancialAidFamily',
    {
      email: normalizedEmail || undefined,
      lastName: normalizedLastName || undefined,
      academicYear: normalizedAcademicYear || undefined,
      limit: 5,
    },
    shouldRun,
  );
}

export function useFinancialAidParentStatus(applicationId: string | undefined) {
  return useQueryAdapter(
    'getFinancialAidParentStatusView',
    { applicationId: applicationId ?? '' },
    Boolean(applicationId)
  );
}

export function useFinancialAidDocumentStatus(applicationId: string | undefined) {
  return useQueryAdapter(
    'getFinancialAidDocumentStatus',
    { applicationId: applicationId ?? '' },
    Boolean(applicationId)
  );
}

export function useFinancialAidAppealHistory(applicationId: string | undefined) {
  return useQueryAdapter(
    'getFinancialAidAppealHistory',
    { applicationId: applicationId ?? '' },
    Boolean(applicationId)
  );
}

export function useCreateFinancialAidApplication() {
  return useJsonInputMutation<'createFinancialAidApplication', {
    applicantId: string;
    academicYear: string;
    sections: Record<string, unknown>;
  }>('createFinancialAidApplication');
}

export function useInviteFinancialAidApplication() {
  return useJsonInputMutation<'inviteFinancialAidApplication', {
    academicYear: string;
    primaryEmail: string;
    lastName?: string;
    familyId?: string;
    familyName?: string;
    applicantId?: string;
  }>('inviteFinancialAidApplication');
}

export function useCopyFinancialAidInviteLink() {
  return useJsonInputMutation<'copyFinancialAidInviteLink', {
    applicationId?: string;
    academicYear?: string;
  }>('copyFinancialAidInviteLink');
}

export function useResendFinancialAidInvite() {
  return useJsonInputMutation<'resendFinancialAidInvite', {
    applicationId: string;
  }>('resendFinancialAidInvite');
}

export function useAssignFinancialAidApplication() {
  return useJsonInputMutation<'assignFinancialAidApplication', {
    applicationId: string;
    assignedTo: string | null;
    assigneeName?: string | null;
  }>('assignFinancialAidApplication');
}

export function useRecordFinancialAidInviteOpen() {
  return useJsonInputMutation<'recordFinancialAidInviteOpen', {
    token: string;
  }>('recordFinancialAidInviteOpen');
}

export function useClaimFinancialAidInvite() {
  return useJsonInputMutation<'claimFinancialAidInvite', {
    token: string;
  }>('claimFinancialAidInvite');
}

export function useUpdateFinancialAidApplication() {
  return useJsonInputMutation<'updateFinancialAidApplication', {
    id: string;
    sections: Record<string, unknown>;
    registrationFeePaid?: boolean;
    correctionMode?: 'staff_correction';
    correctionReason?: string;
    expectedUpdatedAt?: number;
  }>('updateFinancialAidApplication');
}

export function useSubmitFinancialAidApplication() {
  return useJsonInputMutation<'submitFinancialAidApplication', {
    id: string;
    certification: unknown;
  }>('submitFinancialAidApplication');
}

export function useUploadFinancialAidDocument() {
  return useJsonInputMutation<'uploadFinancialAidDocument', {
    applicationId: string;
    documentType: string;
    fileName: string;
    fileType: string;
    fileSizeBytes: number;
  }>('uploadFinancialAidDocument');
}

export function useConfirmFinancialAidDocumentUpload() {
  return useJsonInputMutation<'confirmFinancialAidDocumentUpload', {
    applicationId: string;
    documentType: string;
    documentId: string;
    s3Key: string;
    fileName: string;
    fileType: string;
    fileSizeBytes: number;
  }>('confirmFinancialAidDocumentUpload');
}

export function useAcceptFinancialAidAward() {
  return useJsonInputMutation<'acceptFinancialAidAward', {
    applicationId: string;
    acceptedBy: string;
  }>('acceptFinancialAidAward');
}

export function useDeclineFinancialAidAward() {
  return useJsonInputMutation<'declineFinancialAidAward', {
    applicationId: string;
    declinedBy: string;
    reason?: string;
  }>('declineFinancialAidAward');
}

export function useRespondToInfoRequest() {
  return useJsonInputMutation<'respondToFinancialAidInfoRequest', {
    applicationId: string;
    response: string;
    moreInfoRequestId?: string;
    additionalDocumentIds?: string[];
  }>('respondToFinancialAidInfoRequest');
}

export function useCreateFinancialAidAppeal() {
  return useJsonInputMutation<'createFinancialAidAppeal', {
    financialAidApplicationId: string;
    writtenExplanation: string;
    requestedOutcome: 'reconsider_award' | 'increase_award' | 'reverse_denial';
    requestedAwardPercent?: number;
    additionalDocumentIds?: string[];
  }>('createFinancialAidAppeal');
}

// ============================================================================
// Staff Operations
// ============================================================================

export interface ApplicationsForReviewFilters {
  status?: string;
  hasFlags?: boolean;
  assignedTo?: string;
  limit?: number;
  nextToken?: string;
}

export function useListFinancialAidApplicationsForReview(filters?: ApplicationsForReviewFilters) {
  const effectiveFilters = { limit: 500, ...(filters ?? {}) };
  return useQueryAdapter('listFinancialAidApplicationsForReview', { input: serializeInput(effectiveFilters) }, true);
}

export function useFinancialAidApplicationForReview(applicationId: string | undefined) {
  return useQueryAdapter(
    'getFinancialAidApplicationForReview',
    { applicationId: applicationId ?? '' },
    Boolean(applicationId)
  );
}

export function useFinancialAidStatusHistory(applicationId: string | undefined) {
  return useQueryAdapter(
    'getFinancialAidStatusHistory',
    { applicationId: applicationId ?? '' },
    Boolean(applicationId)
  );
}

export function useBeginFinancialAidReview() {
  return useJsonInputMutation<'beginFinancialAidReview', { applicationId: string }>('beginFinancialAidReview');
}

export function useMakeFinancialAidDecision() {
  type Input = {
    applicationId: string;
    decisionType: 'approve' | 'override' | 'request_more_info' | 'deny';
    awardedPercent?: number;
    overrideJustification?: string;
    requestedInfo?: string;
    responseDeadlineDays?: number;
    denialReason?: string;
    denialExplanation?: string;
    sendExternalParentEmail?: boolean;
    externalParentEmailApproved?: boolean;
  };
  return useJsonInputMutation<'makeFinancialAidDecision', Input>('makeFinancialAidDecision');
}

export function useResolveFinancialAidFlag() {
  return useJsonInputMutation<'resolveFinancialAidFlag', {
    applicationId: string;
    flagId: string;
    resolutionNote?: string;
  }>('resolveFinancialAidFlag');
}

export function useRequestFinancialAidMoreInfo() {
  return useJsonInputMutation<'requestFinancialAidMoreInfo', {
    applicationId: string;
    message: string;
    deadlineDays?: number;
    sendExternalParentEmail?: boolean;
    externalParentEmailApproved?: boolean;
  }>('requestFinancialAidMoreInfo');
}

/**
 * INT-1128: reopen a decided application back to `under_review`.
 * Administrator/owner only; the reason is mandatory and persisted in the audit trail.
 */
export function useReopenFinancialAidApplication() {
  return useJsonInputMutation<'reopenFinancialAidApplication', {
    applicationId: string;
    reason: string;
  }>('reopenFinancialAidApplication');
}

export function useGrantFinancialAidDeadlineExtension() {
  return useJsonInputMutation<
    'grantFinancialAidDeadlineExtension',
    {
      applicationId: string;
      extendedDeadline: string;
      reason: string;
    }
  >('grantFinancialAidDeadlineExtension');
}

export function useUpdateFinancialAidDocumentChecklist() {
  return useJsonInputMutation<'updateFinancialAidDocumentChecklist', {
    applicationId: string;
    checklist: unknown;
    staffOverride?: boolean;
  }>('updateFinancialAidDocumentChecklist');
}

// ============================================================================
// Appeal Operations (Staff)
// ============================================================================

export interface PendingAppealsFilters {
  severity?: string;
  limit?: number;
  nextToken?: string;
}

export function useListPendingFinancialAidAppeals(filters?: PendingAppealsFilters) {
  return useQueryAdapter('listPendingFinancialAidAppeals', { input: serializeInput(filters ?? {}) }, true);
}

export function useFinancialAidAppealSummary(appealId: string | undefined) {
  return useQueryAdapter(
    'getFinancialAidAppealSummary',
    { appealId: appealId ?? '' },
    Boolean(appealId)
  );
}

export function useMakeFinancialAidAppealDecision() {
  return useJsonInputMutation<'makeFinancialAidAppealDecision', {
    appealId: string;
    outcome: 'approved_with_scholarship' | 'upheld_denial' | 'request_more_info';
    decisionNote?: string;
    awardedPercent?: number;
    requestedInfo?: string;
    responseDeadlineDays?: number;
  }>('makeFinancialAidAppealDecision');
}

// ============================================================================
// Report Operations
// ============================================================================

export interface ReportFilters {
  reportType: string;
  academicYear?: string;
  dateFrom?: number;
  dateTo?: number;
  grade?: string;
  status?: string[];
}

export function useGenerateFinancialAidReport(filters: ReportFilters | undefined) {
  return useQueryAdapter(
    'generateFinancialAidReport',
    { input: serializeInput(filters ?? {}) },
    Boolean(filters?.reportType)
  );
}

export function useExportFinancialAidReport() {
  return useJsonInputMutation<'exportFinancialAidReport', {
    reportType: string;
    format: 'excel' | 'pdf';
    filter?: unknown;
    filters?: unknown;
  }>('exportFinancialAidReport');
}

export function useListFinancialAidApplicationReportArtifacts(applicationId: string | undefined) {
  return useQueryAdapter(
    'listFinancialAidApplicationReportArtifacts',
    { input: serializeInput({ applicationId: applicationId ?? '' }) },
    Boolean(applicationId)
  );
}

export function useGenerateFinancialAidApplicationReport() {
  return useJsonInputMutation<'generateFinancialAidApplicationReport', {
    applicationId: string;
    variant: 'summary' | 'full' | 'yst';
  }>('generateFinancialAidApplicationReport');
}

export function useGetFinancialAidApplicationReportDownloadUrl() {
  return useJsonInputMutation<'getFinancialAidApplicationReportDownloadUrl', {
    applicationId: string;
    artifactId: string;
    disposition?: 'inline' | 'attachment';
  }>('getFinancialAidApplicationReportDownloadUrl');
}

// ============================================================================
// Letter Operations
// ============================================================================

export function useGenerateFinancialAidDecisionLetter() {
  return useJsonInputMutation<'generateFinancialAidDecisionLetter', {
    applicationId: string;
    sendEmail?: boolean;
  }>('generateFinancialAidDecisionLetter');
}
